import SparkMD5 from 'spark-md5';
import { Font, type ILabel, INode, IOrientedRectangle } from '@ardoq/yfiles';
import { createSvgElement } from '@ardoq/dom-utils';
import { colors } from '@ardoq/design-tokens';
import { LABEL_VERTICAL_PADDING } from 'yfilesExtensions/styles/consts';
import { GraphItem } from '@ardoq/graph';
import { DISTANCE_TO_EDGE_MARKERS } from './consts';
import { getElementOpacityIfTransparentized } from '../../utils';
import {
  getEdgeLabelIndent,
  hasLineEnds,
} from '../../../../../yfilesExtensions/styles/util';
import { measureLabelElement } from 'yfilesExtensions/styles/measureLabels';
import { getCachedMultiLabelTextElement } from './elementCaches';

export const getMultiLabelHash = (label: ILabel) => {
  const graphItem = label.owner!.tag as GraphItem;
  const labelLayoutWidth = String(label.layout.width);

  const nodeLayoutWidth =
    label.owner instanceof INode ? String(label.owner.layout.width) : '';

  const itemLabels = graphItem.getItemLabels();

  const mainLabel = itemLabels?.mainLabel ?? '';
  const subLabel = itemLabels?.subLabel ?? '';

  return SparkMD5.hash(
    `${mainLabel},${graphItem.getVisualDiffType()},${
      graphItem.isTransparentized
    },${subLabel}${labelLayoutWidth}${nodeLayoutWidth}`
  );
};

export const applySpecialStyle = (
  textElement: SVGTextElement,
  isPlaceholder: boolean,
  isTransparentized: boolean
) => {
  if (isPlaceholder) {
    textElement.style.fill = '';
    textElement.style.stroke = '';
  } else {
    textElement.style.fill = colors.black950;
    textElement.style.fillOpacity = String(
      getElementOpacityIfTransparentized(isTransparentized)
    );
    textElement.style.stroke = 'none';
  }
};

type GetTextElementArgs = {
  container: SVGElement;
  text: string;
  font: Font;
  transform: string;
  isPlaceholder: boolean;
  isOpaque: boolean;
  isSingleLine?: boolean;
  legacyTruncatedFieldValueAndLabelForNode?: string;
};
const addTextElement = ({
  container,
  text,
  font,
  transform,
  isPlaceholder,
  isOpaque,
  isSingleLine = false,
  legacyTruncatedFieldValueAndLabelForNode,
}: GetTextElementArgs) => {
  const textElement = getCachedMultiLabelTextElement(
    text,
    font,
    isSingleLine,
    false,
    legacyTruncatedFieldValueAndLabelForNode
  );

  textElement.setAttribute('transform', transform);

  applySpecialStyle(textElement, isPlaceholder, isOpaque);

  container.appendChild(textElement);

  return textElement;
};

const createMainLabelBackground = (
  layout: IOrientedRectangle,
  edgeMarkersInset: number
) =>
  createSvgElement('rect', {
    width: `${layout.width - edgeMarkersInset * 2}`,
    height: `${layout.height - edgeMarkersInset * 2 + LABEL_VERTICAL_PADDING}`,
    fill: 'white',
    stroke: 'none',
    rx: '4',
    ry: '4',
    transform: `translate(${edgeMarkersInset} ${edgeMarkersInset})`,
  });

type CreateMainLabelArgs = {
  font: Font;
  labelText: string;
  layout: IOrientedRectangle;
  edgeMarkersInset: number;
  isPlaceholder: boolean;
  isTransparentized: boolean;
  container: SVGElement;
  isSingleLine: boolean;
  legacyTruncatedFieldValueAndLabelForNode?: string;
};

const createMainLabel = ({
  font,
  labelText,
  layout,
  edgeMarkersInset,
  isPlaceholder,
  isTransparentized,
  container,
  isSingleLine,
  legacyTruncatedFieldValueAndLabelForNode,
}: CreateMainLabelArgs) => {
  const translateY = isSingleLine
    ? layout.height / 2 + LABEL_VERTICAL_PADDING / 2
    : LABEL_VERTICAL_PADDING + edgeMarkersInset;

  const transform = `translate(${layout.width / 2} ${translateY})`;

  addTextElement({
    container,
    text: labelText,
    font,
    transform,
    isPlaceholder,
    isOpaque: isTransparentized,
    isSingleLine,
    legacyTruncatedFieldValueAndLabelForNode,
  });
};

type CreateAndAppendLabelsArgs = {
  graphItem: GraphItem;
  container: SVGElement;
  layout: IOrientedRectangle;
  edgeMarkersInset: number;
  isPlaceholder: boolean;
  isTransparentized: boolean;
  mainLabelFont: Font;
};
export const createAndAppendLabels = ({
  graphItem,
  container,
  layout,
  edgeMarkersInset,
  isPlaceholder,
  isTransparentized,
  mainLabelFont,
}: CreateAndAppendLabelsArgs) => {
  const itemLabels = graphItem.getItemLabels();

  if (!itemLabels) {
    return;
  }

  const { mainLabel: mainLabelText, legacyTruncatedFieldValueAndLabelForNode } =
    itemLabels;

  if (!mainLabelText) {
    return;
  }

  const labelBackgroundRect = createMainLabelBackground(
    layout,
    edgeMarkersInset
  );
  container.appendChild(labelBackgroundRect);

  // create node name label

  const mainLabelSize = measureLabelElement(mainLabelText, mainLabelFont);

  const isSingleLine =
    !itemLabels.legacyTruncatedFieldValueAndLabelForNode &&
    mainLabelSize.height < 2 * mainLabelFont.fontSize;

  createMainLabel({
    font: mainLabelFont,
    labelText: mainLabelText,
    layout,
    edgeMarkersInset,
    isPlaceholder,
    isTransparentized,
    container,
    isSingleLine,
    legacyTruncatedFieldValueAndLabelForNode,
  });
};

export const getEdgeMarkersInset = (
  graphItem: GraphItem,
  isReference: boolean,
  isParentChildReference: boolean
) =>
  isReference && hasLineEnds(graphItem)
    ? getEdgeLabelIndent(graphItem) + DISTANCE_TO_EDGE_MARKERS
    : isParentChildReference
      ? 10 + DISTANCE_TO_EDGE_MARKERS
      : 0;
